import React, { useState, useEffect, useCallback } from "react";
import { api, fmtNumber } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Database, Loader2, CheckCircle2, XCircle, AlertCircle, Gauge, ArrowRight } from "lucide-react";

function StatusBadge({ status }) {
  if (status === "ok") return null;
  if (status === "not_granted") return <Badge variant="secondary" className="text-[10px]">not granted</Badge>;
  if (status === "granted") return <Badge className="bg-green-600 hover:bg-green-600 text-[10px]">granted</Badge>;
  if (status === "unknown") return <Badge variant="outline" className="text-[10px]">unknown</Badge>;
  return <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/40">error</Badge>;
}

function ObjectCard({ obj }) {
  const granted = obj.status === "ok";
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{obj.label}</p>
        <p className="text-[10px] text-muted-foreground truncate font-mono">{obj.scope}</p>
      </div>
      <div className="flex-shrink-0 text-right">
        {granted ? (
          <span className="text-lg font-semibold text-foreground">{fmtNumber(obj.count ?? 0)}</span>
        ) : (
          <StatusBadge status={obj.status} />
        )}
      </div>
    </div>
  );
}

// `compact` renders a condensed summary for the Analytics Overview; full mode
// renders the complete inventory grid for the CRM Inventory sub-tab.
// When `onOpen` is provided (compact mode on the Overview), the card becomes
// clickable and routes to the CRM Inventory sub-tab (like the other cards).
export default function CrmInventory({ compact = false, onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getCrmInventory().catch(() => null);
      setData(d);
    } catch (e) {
      logError("Failed to load CRM inventory:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.available === false) {
    return (
      <Card
        data-testid="crm-inventory-unavailable"
        onClick={onOpen}
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onKeyDown={onOpen ? (e) => (e.key === "Enter" || e.key === " ") && onOpen() : undefined}
        className={onOpen ? "cursor-pointer transition-colors hover:border-primary/40 hover:bg-muted/40" : undefined}
      >
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-primary" /> CRM Inventory
            {onOpen && <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {data?.reason || "CRM object inventory requires a live HubSpot connection."}
          </p>
        </CardContent>
      </Card>
    );
  }

  const objects = data.objects || [];
  const custom = data.custom || { objects: [], status: "not_granted" };
  const special = data.special || [];
  const totals = data.totals || {};
  const granted = objects.filter((o) => o.status === "ok");

  // API-calls meter (calls used this sync + daily rate-limit context; HubSpot
  // does not bill per call, so we show usage against the daily allowance).
  const meter = (
    <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 flex items-center gap-3" data-testid="api-calls-meter">
      <Gauge className="w-5 h-5 text-primary flex-shrink-0" />
      <div className="text-sm">
        <span className="font-semibold text-foreground">{fmtNumber(data.api_calls ?? 0)}</span>
        <span className="text-muted-foreground"> HubSpot API calls used to build this inventory.</span>
        <span className="text-xs text-muted-foreground block mt-0.5">
          HubSpot API calls are not billed per call — they count against your plan&rsquo;s daily limit
          (commonly ~250,000/day on paid tiers). This inventory uses lightweight count-only calls.
        </span>
      </div>
    </div>
  );

  if (compact) {
    return (
      <Card
        data-testid="crm-inventory-compact"
        onClick={onOpen}
        role={onOpen ? "button" : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onKeyDown={onOpen ? (e) => (e.key === "Enter" || e.key === " ") && onOpen() : undefined}
        className={onOpen ? "cursor-pointer transition-colors hover:border-primary/40 hover:bg-muted/40" : undefined}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-primary" /> CRM Inventory
            <span className="text-xs font-normal text-muted-foreground ml-1">
              · {granted.length} objects readable · {fmtNumber(totals.total_records || 0)} records
            </span>
            {onOpen && <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {meter}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {granted.slice(0, 8).map((o) => (
              <div key={o.key} className="rounded-md border border-border px-2.5 py-1.5">
                <p className="text-[11px] text-muted-foreground truncate">{o.label}</p>
                <p className="text-sm font-semibold text-foreground">{fmtNumber(o.count ?? 0)}</p>
              </div>
            ))}
          </div>
          {granted.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No additional CRM objects are readable with the current key&rsquo;s scopes.
            </p>
          )}
          {onOpen && (
            <Button
              data-testid="crm-inventory-compact-view"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              View
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-inventory">
      <div className="flex items-center gap-2">
        <Database className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-semibold text-foreground leading-tight">CRM Inventory</h2>
          <p className="text-xs text-muted-foreground">
            {granted.length} object types readable · {fmtNumber(totals.total_records || 0)} total records ·
            scope-aware counts from your HubSpot key.
          </p>
        </div>
      </div>

      {meter}

      {/* Standard objects */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">CRM Objects</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="crm-inventory-objects">
            {objects.map((o) => (
              <ObjectCard key={o.key} obj={o} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom objects */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Custom Objects
            {custom.status === "not_granted" && (
              <Badge variant="secondary" className="text-[10px]">schema scope not granted</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {custom.granted && (custom.objects || []).length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {custom.objects.map((c) => (
                <div key={c.name} className="rounded-lg border border-border bg-card px-3 py-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{c.label}</p>
                    <p className="text-[10px] text-muted-foreground truncate font-mono">{c.name}</p>
                  </div>
                  {c.status === "ok" ? (
                    <span className="text-lg font-semibold text-foreground">{fmtNumber(c.count ?? 0)}</span>
                  ) : (
                    <StatusBadge status={c.status} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {custom.status === "not_granted"
                ? "Grant crm.schemas.custom.read (and crm.objects.custom.read) to enumerate and count your custom objects."
                : "No custom objects found on this portal."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Specialized scopes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Other Scopes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="crm-inventory-special">
            {special.map((s) => (
              <div key={s.key} className="rounded-lg border border-border bg-card px-3 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground truncate font-mono">{s.scope}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  {s.status === "granted" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  {s.status === "not_granted" && <XCircle className="w-4 h-4 text-muted-foreground" />}
                  {(s.status === "unknown" || s.status === "error") && <AlertCircle className="w-4 h-4 text-amber-600" />}
                  <StatusBadge status={s.status} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            These scopes use specialized APIs without a uniform record count — SalesTap detects whether
            your key has been granted access (no records are pulled).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
